import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import type { TokenType } from "@/types/token";

interface OneTimeTokenDisplayProps {
  token: string;
  tokenType: TokenType;
  onClose: () => void;
}

const docsUrl = import.meta.env.VITE_DOCS_URL ?? "https://kopai.app";
const otlpEndpoint =
  import.meta.env.VITE_OTLP_ENDPOINT ?? "https://otlp-http.kopai.app";

export function OneTimeTokenDisplay({
  token,
  tokenType,
  onClose,
}: OneTimeTokenDisplayProps) {
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);

  const handleCopyToken = async () => {
    await navigator.clipboard.writeText(token);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const otelConfig = `OTEL_EXPORTER_OTLP_ENDPOINT="${otlpEndpoint}"
OTEL_EXPORTER_OTLP_HEADERS="Authorization=Bearer ${token}"`;

  const cliCommand = "npx @kopai/cli login";

  const handleCopyConfig = async () => {
    const text = tokenType === "cli" ? cliCommand : otelConfig;
    await navigator.clipboard.writeText(text);
    setCopiedConfig(true);
    setTimeout(() => setCopiedConfig(false), 2000);
  };

  return (
    <div className="flex flex-col gap-4 overflow-x-hidden px-1 -mx-1">
      <Card className="border-warning/50 bg-warning/10">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1">
              <p className="font-medium text-warning">
                Copy now - won't be shown again
              </p>
              <p className="text-sm text-warning/80">
                Make sure to copy your token now. You won't be able to see it
                again after closing this dialog.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2">
        <Label>Your Token</Label>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-sm bg-muted p-3 rounded-md break-all">
            {token}
          </code>
          <Button
            variant="outline"
            size="icon"
            onClick={handleCopyToken}
            className="shrink-0"
            aria-label="Copy token"
          >
            {copiedToken ? (
              <Check className="size-4 text-success" />
            ) : (
              <Copy className="size-4" />
            )}
          </Button>
        </div>
      </div>

      {tokenType === "cli" ? (
        <div className="flex flex-col gap-2">
          <Label>CLI Login</Label>
          <p className="text-sm text-muted-foreground">
            Authenticate the CLI with this token:
          </p>
          <div className="flex items-center gap-2">
            <pre className="flex-1 font-mono text-xs bg-muted p-3 rounded-md overflow-x-auto">
              <code>{cliCommand}</code>
            </pre>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyConfig}
              className="shrink-0"
              aria-label="Copy CLI command"
            >
              {copiedConfig ? (
                <Check className="size-4 text-success" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Paste your token at the prompt. Token is saved to{" "}
            <code className="font-mono">.kopairc</code>.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Label>OTEL Configuration</Label>
          <p className="text-sm text-muted-foreground">
            Add these environment variables for your OTEL SDK or collector
            configuration:
          </p>
          <div className="flex items-center gap-2">
            <pre className="flex-1 font-mono text-xs bg-muted p-3 rounded-md overflow-x-auto">
              <code>{otelConfig}</code>
            </pre>
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyConfig}
              className="shrink-0"
              aria-label="Copy OTEL configuration"
            >
              {copiedConfig ? (
                <Check className="size-4 text-success" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>
        </div>
      )}

      <Separator />

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Next steps</p>
        {tokenType === "cli" ? (
          <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
            <li>
              <a
                href={`${docsUrl}/docs/authentication/#login`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground"
              >
                → How to use the CLI
              </a>
            </li>
            <li>
              <a
                href={`${docsUrl}/docs/authentication/#verify-authentication`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground"
              >
                → Verify authentication
              </a>
            </li>
          </ul>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
            <li>
              <a
                href={`${docsUrl}/docs/kopai-cloud/#step-3-point-your-app-at-kopai-cloud`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground"
              >
                → Set up the SDK in your app
              </a>
            </li>
            <li>
              <Link
                to="/observability"
                onClick={onClose}
                className="hover:text-foreground"
              >
                → Verify ingestion in Observability
              </Link>
            </li>
          </ul>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}
